"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DelawareRules = void 0;
const wasteAccumulation_1 = require("encamp-shared/dist/constants/wasteAccumulation");
const unwrap_1 = require("../../unwrap");
const _stateRules_1 = require("./_stateRules");
const DE_DERIVATIONS = {
    effectiveWasteKind: {
        from: ["wasteKind", "managedAsUniversalWaste", "operatesUnderSubpartP"],
        label: "Governing program",
        citation: "40 CFR 273.9, 261.6(a)(4), 266.501",
        description: (0, unwrap_1.unwrap) `Delaware adopts all federal provisions.`,
        cases: [
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: true },
                produces: "UNIVERSAL_WASTE",
            },
            {
                when: { wasteKind: "UNIVERSAL_WASTE", managedAsUniversalWaste: false },
                produces: "RCRA",
            },
            { when: { wasteKind: "USED_OIL" }, produces: "USED_OIL" },
            {
                when: { wasteKind: "PHARMACEUTICAL", operatesUnderSubpartP: true },
                produces: "PHARMACEUTICAL",
            },
            {
                when: { wasteKind: "PHARMACEUTICAL", operatesUnderSubpartP: false },
                produces: "RCRA",
            },
            { when: { wasteKind: "RCRA" }, produces: "RCRA" },
            { when: { wasteKind: null }, produces: "RCRA" },
        ],
    },
};
/**
 * Spec, not snapshot (see the discipline header in ../types.ts): a row
 * changes only when a regulatory specialist says the law reads differently —
 * never to make the class below pass.
 */
const DE_SATELLITE_CAPS = {
    kind: "VOLUME",
    nonAcuteGal: 55,
    acuteGal: 0.25,
    acuteKg: 1,
    moveWindowDays: 0,
    citation: "7 DE Admin. Code 1302 § 262.15(a), (a)(6)",
    jurisdiction: "DE",
};
const FEDERAL_NO_STANDING_SATELLITE_CLOCK = {
    kind: "NO_LIMIT",
    citation: "40 CFR 262.15",
    jurisdiction: "FEDERAL",
};
const FEDERAL_VSQG_NO_LIMIT = {
    kind: "NO_LIMIT",
    citation: "40 CFR 262.14",
    jurisdiction: "FEDERAL",
};
const FEDERAL_UNIVERSAL_WASTE_CLOCK = {
    kind: "TIME",
    days: 365,
    citation: "40 CFR 273.15(a), 273.35(a)",
    jurisdiction: "FEDERAL",
};
const FEDERAL_SUBPART_P_CLOCK = {
    kind: "TIME",
    days: 365,
    citation: "40 CFR 266.502(f)(1)",
    jurisdiction: "FEDERAL",
};
const FEDERAL_USED_OIL_NO_LIMIT = {
    kind: "NO_LIMIT",
    citation: "40 CFR 279.22",
    jurisdiction: "FEDERAL",
};
const DE_SATELLITE_NOTE = (0, unwrap_1.unwrap) `
  Only change from federal. The move window is ZERO days: Delaware's § 262.15(a)(6)
  requires the excess to be removed "immediately", and the section's note defines that as "within
  the same shift in which the 55 gallon limit was exceeded". A zero-day move window 
  gets shown in-app as "before the end of the shift".`;
const DE_FEDERAL_NOTE = (0, unwrap_1.unwrap) `
  Delaware restates this provision without changing it, so the inherited federal answer and its
  CFR citation are shown; that is where the operative text lives.`;
const DE_VSQG_NOTE = (0, unwrap_1.unwrap) `
  Same as the federal VSQG conditions.`;
const DE_EXPECTATIONS = [
    // ── CENTRAL ACCUMULATION — federal part 262 inherited ─────────────────
    {
        given: { effectiveWasteKind: "RCRA" },
        when: { areaType: "CENTRAL", generatorStatus: "LQG" },
        expect: {
            clock: { kind: "TIME", days: 90, citation: "40 CFR 262.17(a)", jurisdiction: "FEDERAL" },
        },
        note: DE_FEDERAL_NOTE,
    },
    {
        given: { effectiveWasteKind: "RCRA" },
        when: { areaType: "CENTRAL", generatorStatus: "SQG", mustTransport200Miles: false },
        expect: {
            clock: { kind: "TIME", days: 180, citation: "40 CFR 262.16(b)", jurisdiction: "FEDERAL" },
        },
        note: DE_FEDERAL_NOTE,
    },
    {
        given: { effectiveWasteKind: "RCRA" },
        when: { areaType: "CENTRAL", generatorStatus: "SQG", mustTransport200Miles: true },
        expect: {
            clock: { kind: "TIME", days: 270, citation: "40 CFR 262.16(c)", jurisdiction: "FEDERAL" },
        },
        note: DE_FEDERAL_NOTE,
    },
    {
        given: { effectiveWasteKind: "RCRA" },
        when: { areaType: "CENTRAL", generatorStatus: "VSQG" },
        expect: { clock: FEDERAL_VSQG_NO_LIMIT },
        note: DE_VSQG_NOTE,
    },
    {
        given: { effectiveWasteKind: "RCRA" },
        when: { areaType: "CENTRAL", generatorStatus: null },
        expect: { clock: { kind: "UNDETERMINED" } },
    },
    // ── SATELLITE ACCUMULATION — the one Delaware departure ───────────────
    {
        given: { effectiveWasteKind: "RCRA" },
        when: { areaType: "SATELLITE", generatorStatus: "SQG" },
        expect: { caps: DE_SATELLITE_CAPS, clock: FEDERAL_NO_STANDING_SATELLITE_CLOCK },
        note: DE_SATELLITE_NOTE,
    },
    {
        given: { effectiveWasteKind: "RCRA" },
        when: { areaType: "SATELLITE", generatorStatus: "LQG" },
        expect: { caps: DE_SATELLITE_CAPS, clock: FEDERAL_NO_STANDING_SATELLITE_CLOCK },
        note: DE_SATELLITE_NOTE,
    },
    {
        given: { effectiveWasteKind: "RCRA" },
        when: { areaType: "SATELLITE", generatorStatus: "VSQG" },
        expect: { caps: FEDERAL_VSQG_NO_LIMIT, clock: FEDERAL_NO_STANDING_SATELLITE_CLOCK },
        note: DE_VSQG_NOTE,
    },
    {
        given: { effectiveWasteKind: "RCRA" },
        when: { areaType: "SATELLITE", generatorStatus: null },
        expect: { caps: DE_SATELLITE_CAPS, clock: FEDERAL_NO_STANDING_SATELLITE_CLOCK },
        note: "The more protective default until the status is resolved.",
    },
    // ── UNIVERSAL WASTE — federal part 273 inherited ──────────────────────
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: { areaType: "CENTRAL" },
        expect: { clock: FEDERAL_UNIVERSAL_WASTE_CLOCK },
        note: DE_FEDERAL_NOTE,
    },
    {
        given: { effectiveWasteKind: "UNIVERSAL_WASTE" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 261.9, 273.1(b)", jurisdiction: "FEDERAL" },
            clock: FEDERAL_UNIVERSAL_WASTE_CLOCK,
        },
        note: DE_FEDERAL_NOTE,
    },
    // ── USED OIL — federal part 279 inherited ─────────────────────────────
    {
        given: { effectiveWasteKind: "USED_OIL" },
        when: { areaType: "CENTRAL" },
        expect: { clock: FEDERAL_USED_OIL_NO_LIMIT },
        note: DE_FEDERAL_NOTE,
    },
    {
        given: { effectiveWasteKind: "USED_OIL" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 261.6(a)(4)", jurisdiction: "FEDERAL" },
            clock: FEDERAL_USED_OIL_NO_LIMIT,
        },
        note: DE_FEDERAL_NOTE,
    },
    // ── SUBPART P PHARMACEUTICALS — federal part 266 inherited ────────────
    {
        given: { effectiveWasteKind: "PHARMACEUTICAL" },
        when: { areaType: "CENTRAL" },
        expect: { clock: FEDERAL_SUBPART_P_CLOCK },
        note: DE_FEDERAL_NOTE,
    },
    {
        given: { effectiveWasteKind: "PHARMACEUTICAL" },
        when: { areaType: "SATELLITE" },
        expect: {
            caps: { kind: "NO_LIMIT", citation: "40 CFR 266.501(d)", jurisdiction: "FEDERAL" },
            clock: FEDERAL_SUBPART_P_CLOCK,
        },
        note: DE_FEDERAL_NOTE,
    },
];
/**
 * DELAWARE ACCUMULATION THRESHOLD EXPECTATIONS — 7 DE Admin. Code 1302
 * §§ 262.14, 262.16
 *
 * Delaware's numbers are the federal ones under Delaware's own restated text,
 * so the inherited federal slot already answers correctly and this class
 * overrides no threshold behavior. The table is authored anyway rather than
 * inherited: a state's claims are what the specialist reviews and what the SME
 * packet prints under its heading, so inheriting would put federal rows behind
 * Delaware's name without anyone having read them for Delaware.
 *
 * Delaware uses the federal generator tiers, so `judgedAgainst` carries the
 * federal names — unlike Connecticut, whose regulation still says CESQG.
 */
const DE_ACCUMULATION_THRESHOLD_EXPECTATIONS = [
    {
        when: { generatorStatus: "LQG" },
        expect: {
            thresholds: { totalKg: null, nonAcuteKg: null, acuteKg: null },
            judgedAgainst: { abbreviation: "LQG", name: "Large Quantity Generator" },
        },
    },
    {
        when: { generatorStatus: "SQG" },
        expect: {
            thresholds: { totalKg: 6000, nonAcuteKg: null, acuteKg: null },
            judgedAgainst: { abbreviation: "SQG", name: "Small Quantity Generator" },
        },
    },
    {
        when: { generatorStatus: "VSQG" },
        expect: {
            thresholds: { totalKg: null, nonAcuteKg: 1000, acuteKg: 1 },
            judgedAgainst: { abbreviation: "VSQG", name: "Very Small Quantity Generator" },
        },
    },
    {
        when: { generatorStatus: null },
        expect: { thresholds: { totalKg: null, nonAcuteKg: null, acuteKg: null }, judgedAgainst: null },
    },
];
const DE_SOURCES = [
    {
        title: "7 DE Admin. Code 1302, Part 262",
        url: "https://archive.regulations.delaware.gov/AdminCode/title7/1000/1300/1302/262.shtml",
    },
];
/** Delaware (DNREC) accumulation limits, 7 DE Admin. Code 1302 (DRGHW). */
class DelawareRules extends _stateRules_1.StateRules {
    constructor() {
        super(...arguments);
        this.expectations = DE_EXPECTATIONS;
        this.accumulationThresholdExpectations = DE_ACCUMULATION_THRESHOLD_EXPECTATIONS;
        this.derivations = DE_DERIVATIONS;
        this.sources = DE_SOURCES;
        this.stateStatusDomain = [];
    }
    satelliteCaps(ctx) {
        // Kind programs and the VSQG carve-out are federal law here; only the
        // window on the ordinary caps is Delaware's own.
        if (this.effectiveWasteKind(ctx) !== "RCRA" || ctx.generatorStatus === "VSQG") {
            return super.satelliteCaps(ctx);
        }
        // Federal thresholds under Delaware's regulation; an unknown status still
        // gets them as the more protective default. The window is the one place
        // Delaware departs: "immediately", defined as within the same shift.
        return {
            kind: "VOLUME",
            nonAcuteGal: wasteAccumulation_1.SAA_NON_ACUTE_VOLUME_LIMIT_GAL,
            acuteGal: wasteAccumulation_1.SAA_ACUTE_VOLUME_LIMIT_GAL,
            acuteKg: wasteAccumulation_1.SAA_ACUTE_MASS_LIMIT_KG,
            moveWindowDays: 0,
            citation: "7 DE Admin. Code 1302 § 262.15(a), (a)(6)",
            jurisdiction: "DE",
        };
    }
}
exports.DelawareRules = DelawareRules;
//# sourceMappingURL=DE.js.map